// Service hybride pour optimiser les coûts des API de calcul d'itinéraires
// Utilise OpenRouteService (gratuit) pour la plupart des calculs
// Utilise Google Maps Directions uniquement pour les cas critiques

import { OPTIMIZATION_CONFIG, MESSAGES } from '../config/optimization';

class RouteService {
  constructor() {
    this.cache = new Map();
    this.openRouteServiceUrl = 'https://api.openrouteservice.org/v2/directions/driving-car';
    this.openRouteServiceKey = 'eyJvcmciOiI1YjNjZTM1OTc4NTExMTAwMDFjZjYyNDgiLCJpZCI6IjEzMTdjOTY4Njk2NjRjOTI5ZTJiMjY4MGU0YWQ3Yzg2IiwiaCI6Im11cm11cjY0In0=';
    this.googleMapsDirectionsService = null;
    this.useGoogleMapsForCritical = OPTIMIZATION_CONFIG.ENABLE_GOOGLE_DIRECTIONS === true;
    this.cacheExpiration = OPTIMIZATION_CONFIG.CACHE_EXPIRATION_HOURS * 60 * 60 * 1000; // en millisecondes
    
    // Rate limiting pour OpenRouteService
    this.openRouteServiceCalls = {
      daily: 0,
      minute: 0,
      lastMinuteReset: Date.now(),
      lastDailyReset: new Date().setHours(0, 0, 0, 0)
    };
    
    // Gestion des attentes internes (rate limiting)
    this.waitingQueue = [];
    this.isWaitingForReset = false;
    this.onWaitStart = null; // (ms:number, scope:'minute'|'daily'|null) => void
    this.onWaitEnd = null;   // () => void

    // Cooldown suite à un 429 (Too Many Requests)
    this.cooldownUntilMs = 0;
    this.lastRateLimitScope = null; // 'minute' | 'daily' | null
  }

  setGoogleMapsDirectionsService(service) {
    this.googleMapsDirectionsService = service;
  }

  // ORS Optimization API (test indépendant)
  // docs: https://openrouteservice.org/dev/#/api-docs/optimization/introductions/optimization
  async optimizeSchedulesWithORS(jobs, vehicles, options = {}) {
    const url = 'https://api.openrouteservice.org/optimization';
    // Respecter les quotas: petite temporisation si cooldown en cours
    if (!this.canUseOpenRouteService()) {
      await this.waitForReset();
    }

    const body = {
      jobs,
      vehicles,
      options
    };

    const resp = await fetch(url, {
      method: 'POST',
      headers: {
        'Authorization': this.openRouteServiceKey,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(body)
    });

    if (!resp.ok) {
      if (resp.status === 429) {
        const retryAfterHeader = resp.headers.get('Retry-After');
        const retryAfterMs = retryAfterHeader ? parseInt(retryAfterHeader, 10) * 1000 : 30000;
        this.lastRateLimitScope = 'minute';
        this.cooldownUntilMs = Date.now() + Math.max(1000, retryAfterMs);
      }
      const data = await resp.json().catch(() => ({}));
      throw new Error(data?.error?.message || `ORS Optimization error: ${resp.status}`);
    }

    const data = await resp.json();
    // On renvoie brut pour inspection dans l'UI
    return data;
  }

  // Gestion du rate limiting pour OpenRouteService
  updateRateLimiting() {
    const now = Date.now();
    
    // Reset minute si nécessaire
    if (now - this.openRouteServiceCalls.lastMinuteReset >= 60000) { // 1 minute
      this.openRouteServiceCalls.minute = 0;
      this.openRouteServiceCalls.lastMinuteReset = now;
    }
    
    // Reset daily si nécessaire
    const today = new Date().setHours(0, 0, 0, 0);
    if (today > this.openRouteServiceCalls.lastDailyReset) {
      this.openRouteServiceCalls.daily = 0;
      this.openRouteServiceCalls.lastDailyReset = today;
    }
  }

  // Vérifie si OpenRouteService peut être utilisé
  canUseOpenRouteService() {
    this.updateRateLimiting();
    const now = Date.now();
    if (now < this.cooldownUntilMs) {
      // En cooldown après un 429
      return false;
    }
    
    const dailyLimit = OPTIMIZATION_CONFIG.LIMITS.OPENROUTE_SERVICE_DAILY;
    const minuteLimit = OPTIMIZATION_CONFIG.LIMITS.OPENROUTE_SERVICE_MINUTE;
    
    const dailyWarning = OPTIMIZATION_CONFIG.ALERTS.OPENROUTE_SERVICE_WARNING;
    const minuteWarning = OPTIMIZATION_CONFIG.ALERTS.OPENROUTE_SERVICE_MINUTE_WARNING;
    
    // Vérifier les limites
    if (this.openRouteServiceCalls.daily >= dailyLimit) {
      console.warn('⚠️ Limite quotidienne OpenRouteService atteinte:', this.openRouteServiceCalls.daily);
      return false;
    }
    
    if (this.openRouteServiceCalls.minute >= minuteLimit) {
      console.warn('⚠️ Limite minute OpenRouteService atteinte:', this.openRouteServiceCalls.minute);
      return false;
    }
    
    // Afficher des avertissements
    if (this.openRouteServiceCalls.daily >= dailyWarning) {
      console.warn('⚠️ Approche de la limite quotidienne OpenRouteService:', this.openRouteServiceCalls.daily);
    }
    
    if (this.openRouteServiceCalls.minute >= minuteWarning) {
      console.warn('⚠️ Approche de la limite minute OpenRouteService:', this.openRouteServiceCalls.minute);
    }
    
    return true;
  }

  // Calcule le temps d'attente avant la prochaine réinitialisation
  getTimeUntilReset() {
    this.updateRateLimiting();
    
    const now = Date.now();
    const timeUntilMinuteReset = 60000 - (now - this.openRouteServiceCalls.lastMinuteReset);
    const timeUntilDailyReset = 24 * 60 * 60 * 1000 - (now - this.openRouteServiceCalls.lastDailyReset);
    
    return {
      minute: Math.max(0, timeUntilMinuteReset),
      daily: Math.max(0, timeUntilDailyReset),
      nextReset: Math.min(timeUntilMinuteReset, timeUntilDailyReset)
    };
  }

  // Attend que les limites soient réinitialisées (accepte un temps d'attente explicite)
  async waitForReset(waitOverrideMs) {
    // Si un temps explicite est fourni (ex: Retry-After), l'utiliser
    if (typeof waitOverrideMs === 'number' && waitOverrideMs > 0) {
      if (this.isWaitingForReset) {
        return new Promise((resolve) => this.waitingQueue.push(resolve));
      }
      this.isWaitingForReset = true;
      if (this.onWaitStart) {
        this.onWaitStart(waitOverrideMs, this.lastRateLimitScope || 'minute');
      }
      return new Promise((resolve) => {
        setTimeout(() => {
          this.cooldownUntilMs = 0;
          this.lastRateLimitScope = null;
          this.updateRateLimiting();
          this.isWaitingForReset = false;
          // Notifier les autres en attente
          this.waitingQueue.forEach((fn) => fn());
          this.waitingQueue = [];
          if (this.onWaitEnd) {
            this.onWaitEnd();
          }
          resolve();
        }, waitOverrideMs);
      });
    }

    // Sinon, se baser sur le prochain reset calculé (minute / quotidien)
    if (this.isWaitingForReset) {
      return new Promise((resolve) => {
        this.waitingQueue.push(resolve);
      });
    }

    this.isWaitingForReset = true;
    
    const resetTimes = this.getTimeUntilReset();
    const waitTime = resetTimes.nextReset;
    
    if (waitTime > 0) {
      console.log(`⏳ Attente de ${Math.ceil(waitTime / 1000)} secondes pour la réinitialisation des limites OpenRouteService`);
      if (this.onWaitStart) {
        this.onWaitStart(waitTime, this.lastRateLimitScope || 'minute');
      }
      return new Promise((resolve) => {
        setTimeout(() => {
          this.updateRateLimiting();
          this.isWaitingForReset = false;
          
          // Notifier les autres en attente
          this.waitingQueue.forEach(resolveFunc => resolveFunc());
          this.waitingQueue = [];
          
          console.log('✅ Limites OpenRouteService réinitialisées');
          if (this.onWaitEnd) {
            this.onWaitEnd();
          }
          resolve();
        }, waitTime);
      });
    }
    
    this.isWaitingForReset = false;
    return Promise.resolve();
  }

  // Définir des callbacks d'attente (UI peut afficher un compte à rebours)
  setWaitCallbacks(onStart, onEnd) {
    this.onWaitStart = onStart;
    this.onWaitEnd = onEnd;
  }

  // Incrémente les compteurs OpenRouteService
  incrementOpenRouteServiceCalls() {
    this.openRouteServiceCalls.daily++;
    this.openRouteServiceCalls.minute++;
  }

  // Génère une clé de cache pour deux coordonnées
  getCacheKey(coords1, coords2) {
    const sorted = [coords1, coords2].sort((a, b) => 
      a.lat === b.lat ? a.lng - b.lng : a.lat - b.lat
    );
    return `${sorted[0].lat},${sorted[0].lng}-${sorted[1].lat},${sorted[1].lng}`;
  }

  // Vérifie si un résultat est en cache et valide
  getFromCache(coords1, coords2) {
    const key = this.getCacheKey(coords1, coords2);
    const cached = this.cache.get(key);
    
    if (cached && (Date.now() - cached.timestamp) < this.cacheExpiration) {
      console.log(MESSAGES.CACHE_HIT + ':', key);
      return cached.data;
    }
    
    return null;
  }

  // Met en cache un résultat
  setCache(coords1, coords2, data) {
    const key = this.getCacheKey(coords1, coords2);
    this.cache.set(key, {
      data,
      timestamp: Date.now()
    });
  }

  // Calcul de distance approximative (formule de Haversine)
  calculateApproximateDistance(coords1, coords2) {
    const R = 6371e3; // Rayon de la Terre en mètres
    const φ1 = coords1.lat * Math.PI / 180;
    const φ2 = coords2.lat * Math.PI / 180;
    const Δφ = (coords2.lat - coords1.lat) * Math.PI / 180;
    const Δλ = (coords2.lng - coords1.lng) * Math.PI / 180;

    const a = Math.sin(Δφ/2) * Math.sin(Δφ/2) +
              Math.cos(φ1) * Math.cos(φ2) *
              Math.sin(Δλ/2) * Math.sin(Δλ/2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));

    return R * c; // Distance en mètres
  }

  // Calcul de durée approximative basé sur la distance
  calculateApproximateDuration(distance) {
    const averageSpeed = OPTIMIZATION_CONFIG.AVERAGE_SPEED_KMH; // km/h en ville
    const speedInMps = averageSpeed * 1000 / 3600; // m/s
    return distance / speedInMps; // durée en secondes
  }

  // Appel à OpenRouteService
  async callOpenRouteService(coords1, coords2) {
    try {
      // Si on est en cooldown, lever immédiatement une RateLimitError
      if (Date.now() < this.cooldownUntilMs) {
        const retryAfterMs = this.cooldownUntilMs - Date.now();
        const err = new Error('OpenRouteService rate limited (cooldown)');
        err.name = 'RateLimitError';
        err.retryAfterMs = retryAfterMs;
        err.scope = this.lastRateLimitScope || 'minute';
        throw err;
      }

      const response = await fetch(this.openRouteServiceUrl, {
        method: 'POST',
        headers: {
          'Authorization': this.openRouteServiceKey,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          coordinates: [
            [coords1.lng, coords1.lat],
            [coords2.lng, coords2.lat]
          ],
          format: 'json',
          units: 'km'
        })
      });

      if (!response.ok) {
        // Gestion spécifique 429 Too Many Requests
        if (response.status === 429) {
          // Tenter de récupérer Retry-After (secondes) ou calculer un fallback (30s)
          const retryAfterHeader = response.headers.get('Retry-After');
          const retryAfterMs = retryAfterHeader ? parseInt(retryAfterHeader, 10) * 1000 : 30000;

          // Déterminer le scope si possible (minute par défaut)
          // Certaines implémentations exposent X-RateLimit-Reset-*; on garde simple ici
          const scope = 'minute';
          this.lastRateLimitScope = scope;
          this.cooldownUntilMs = Date.now() + Math.max(1000, retryAfterMs);

          if (this.onLimitsReached) {
            this.onLimitsReached(scope);
          }

          const rateErr = new Error('OpenRouteService rate limited (429)');
          rateErr.name = 'RateLimitError';
          rateErr.retryAfterMs = retryAfterMs;
          rateErr.scope = scope;
          throw rateErr;
        }
        // Regarde si error est dans le body et error.code
        else if (response.status === 404) {
          const data = await response.json();
          let errMessage = data?.error?.message || 'Impossible de trouver un itinéraire.';
          if (data.error && data.error.code === 2009) {
            throw new Error(`Impossible de trouver un itinéraire. Message: ${errMessage}`);
          }
        }

        throw new Error(`OpenRouteService error: ${response.status}`);
      }

      const data = await response.json();

      // format: 'json' → payload avec data.routes
      if (Array.isArray(data.routes) && data.routes.length > 0) {
        const route = data.routes[0];
        const distanceKm = (route.summary && typeof route.summary.distance === 'number')
          ? route.summary.distance
          : (route.segments && route.segments[0] && typeof route.segments[0].distance === 'number'
              ? route.segments[0].distance
              : null);
        const durationSec = (route.summary && typeof route.summary.duration === 'number')
          ? route.summary.duration
          : (route.segments && route.segments[0] && typeof route.segments[0].duration === 'number'
              ? route.segments[0].duration
              : null);

        if (distanceKm != null && durationSec != null) {
          return {
            distance: Math.round(distanceKm * 1000), // km → m
            duration: Math.round(durationSec)
          };
        }
      }

      // format: 'geojson' (fallback) → payload avec data.features
      if (Array.isArray(data.features) && data.features.length > 0) {
        const route = data.features[0];
        const properties = route.properties || {};
        const seg = Array.isArray(properties.segments) ? properties.segments[0] : null;
        if (seg && typeof seg.distance === 'number' && typeof seg.duration === 'number') {
          // En réponse json/geojson avec units='km', ORS renvoie la distance en kilomètres
          const distanceKm = seg.distance;
          const durationSec = seg.duration;
          return {
            distance: Math.round(distanceKm * 1000),
            duration: Math.round(durationSec)
          };
        }
      }
      
      // Si le JSON contient une erreur explicite
      if (data && data.error) {
        const errMessage = data.error.message || 'Erreur ORS';
        throw new Error(errMessage);
      }

      throw new Error('Aucun itinéraire trouvé');
    } catch (error) {
      console.error('Erreur OpenRouteService:', error);
      throw error;
    }
  }

  // Appel à Google Maps Directions (pour les cas critiques)
  async callGoogleMapsDirections(coords1, coords2) {
    if (!OPTIMIZATION_CONFIG.ENABLE_GOOGLE_DIRECTIONS) {
      throw new Error('Google Maps Directions désactivé');
    }
    if (!this.googleMapsDirectionsService) {
      throw new Error('Google Maps Directions Service non disponible');
    }

    try {
      const response = await this.googleMapsDirectionsService.route({
        origin: new window.google.maps.LatLng(coords1.lat, coords1.lng),
        destination: new window.google.maps.LatLng(coords2.lat, coords2.lng),
        travelMode: window.google.maps.TravelMode.DRIVING,
        provideRouteAlternatives: false
      });

      if (response.routes && response.routes.length > 0) {
        const route = response.routes[0];
        return {
          distance: route.legs[0].distance.value, // Distance en mètres
          duration: route.legs[0].duration.value // Durée en secondes
        };
      }

      throw new Error('Aucun itinéraire trouvé');
    } catch (error) {
      console.error('Erreur Google Maps Directions:', error);
      throw error;
    }
  }

  // Détermine si un calcul est critique (nécessite Google Maps)
  isCriticalCalculation(coords1, coords2, context = {}) {
    // Utiliser Google Maps pour :
    // 1. Les calculs depuis/vers le Moulin (coordonnées importantes)
    // 2. Les distances > seuil configuré (précision importante)
    // 3. Les calculs finaux d'optimisation
    
    const distance = this.calculateApproximateDistance(coords1, coords2);
    const isFromMoulin = this.isMoulinCoordinates(coords1) || this.isMoulinCoordinates(coords2);
    const isLongDistance = distance > OPTIMIZATION_CONFIG.CRITICAL_DISTANCE_KM * 1000; // en mètres
    const isFinalOptimization = context.isFinalOptimization;
    
    return isFromMoulin || isLongDistance || isFinalOptimization;
  }

  // Vérifie si les coordonnées correspondent au Moulin
  isMoulinCoordinates(coords) {
    const MOULIN_COORDS = { lat: 45.7613911, lng: 4.9167728 };
    const tolerance = OPTIMIZATION_CONFIG.MOULIN_COORDINATES_TOLERANCE; // ~100m
    
    return Math.abs(coords.lat - MOULIN_COORDS.lat) < tolerance &&
           Math.abs(coords.lng - MOULIN_COORDS.lng) < tolerance;
  }

  // Méthode principale pour calculer les informations d'itinéraire
  async calculateRouteInfo(coords1, coords2, context = {}) {
    // Vérifier le cache d'abord
    const cached = this.getFromCache(coords1, coords2);
    if (cached) {
      return cached;
    }

    try {
      let result;

      // Déterminer quelle API utiliser
      if (this.isCriticalCalculation(coords1, coords2, context) && OPTIMIZATION_CONFIG.ENABLE_GOOGLE_DIRECTIONS) {
        console.log(MESSAGES.USING_GOOGLE_MAPS);
        result = await this.callGoogleMapsDirections(coords1, coords2);
      } else {
        // Vérifier si OpenRouteService peut être utilisé
        if (this.canUseOpenRouteService()) {
          console.log(MESSAGES.USING_OPENROUTE);
          try {
            result = await this.callOpenRouteService(coords1, coords2);
            this.incrementOpenRouteServiceCalls();
          } catch (openRouteError) {
            // Si on tombe sur un rate limit, attendre puis réessayer pour les calculs non critiques
            if (openRouteError && openRouteError.name === 'RateLimitError' && !this.isCriticalCalculation(coords1, coords2, context)) {
              const waitMs = Math.max(0, openRouteError.retryAfterMs || 0);
              await this.waitForReset(waitMs);
              // Réessayer après l'attente
              try {
                result = await this.callOpenRouteService(coords1, coords2);
                this.incrementOpenRouteServiceCalls();
              } catch (retryErr) {
                console.warn('OpenRouteService encore indisponible après attente');
                if (OPTIMIZATION_CONFIG.ENABLE_GOOGLE_DIRECTIONS) {
                  console.warn('Bascule Google Maps');
                  result = await this.callGoogleMapsDirections(coords1, coords2);
                } else {
                  // Fallback approximatif si Google désactivé
                  const distance = this.calculateApproximateDistance(coords1, coords2);
                  const duration = this.calculateApproximateDuration(distance);
                  result = { distance: Math.round(distance), duration: Math.round(duration) };
                }
              }
            } else {
              console.warn('OpenRouteService échoué');
              if (OPTIMIZATION_CONFIG.ENABLE_GOOGLE_DIRECTIONS) {
                console.warn('Bascule Google Maps');
                result = await this.callGoogleMapsDirections(coords1, coords2);
              } else {
                // Fallback approximatif si Google désactivé
                const distance = this.calculateApproximateDistance(coords1, coords2);
                const duration = this.calculateApproximateDuration(distance);
                result = { distance: Math.round(distance), duration: Math.round(duration) };
              }
            }
          }
        } else {
          // Attendre la réinitialisation des limites si c'est un calcul non critique
          if (!this.isCriticalCalculation(coords1, coords2, context)) {
            console.log('⏳ Limites OpenRouteService atteintes, attente de la réinitialisation...');
            await this.waitForReset();
            
            // Réessayer avec OpenRouteService après réinitialisation
            if (this.canUseOpenRouteService()) {
              console.log('🔄 Réessai avec OpenRouteService après réinitialisation');
              try {
                result = await this.callOpenRouteService(coords1, coords2);
                this.incrementOpenRouteServiceCalls();
              } catch (openRouteError) {
                console.warn('OpenRouteService échoué après réinitialisation');
                if (OPTIMIZATION_CONFIG.ENABLE_GOOGLE_DIRECTIONS) {
                  console.warn('Bascule Google Maps');
                  result = await this.callGoogleMapsDirections(coords1, coords2);
                } else {
                  const distance = this.calculateApproximateDistance(coords1, coords2);
                  const duration = this.calculateApproximateDuration(distance);
                  result = { distance: Math.round(distance), duration: Math.round(duration) };
                }
              }
            } else {
              console.log('OpenRouteService toujours indisponible après réinitialisation');
              if (OPTIMIZATION_CONFIG.ENABLE_GOOGLE_DIRECTIONS) {
                console.log('Bascule Google Maps');
                result = await this.callGoogleMapsDirections(coords1, coords2);
              } else {
                const distance = this.calculateApproximateDistance(coords1, coords2);
                const duration = this.calculateApproximateDuration(distance);
                result = { distance: Math.round(distance), duration: Math.round(duration) };
              }
            }
          } else {
            console.log('OpenRouteService non disponible (limites atteintes)');
            if (OPTIMIZATION_CONFIG.ENABLE_GOOGLE_DIRECTIONS) {
              console.log('Utilisation de Google Maps (calcul critique)');
              result = await this.callGoogleMapsDirections(coords1, coords2);
            } else {
              // Même en critique, si Google est désactivé: fallback approximatif
              const distance = this.calculateApproximateDistance(coords1, coords2);
              const duration = this.calculateApproximateDuration(distance);
              result = { distance: Math.round(distance), duration: Math.round(duration) };
            }
          }
        }
      }

      // Mettre en cache le résultat
      this.setCache(coords1, coords2, result);
      
      return result;
    } catch (error) {
      console.error('Erreur lors du calcul d\'itinéraire:', error);
      
      // En cas d'échec, utiliser des valeurs approximatives
      const distance = this.calculateApproximateDistance(coords1, coords2);
      const duration = this.calculateApproximateDuration(distance);
      
      console.log(MESSAGES.USING_APPROXIMATE + ':', { distance, duration });
      
      return {
        distance: Math.round(distance),
        duration: Math.round(duration)
      };
    }
  }
}

export default RouteService;
